// ManageSubscriptions.js
// Customer-facing portal to view and cancel subscriptions.
// Supports two modes:
//   Deep-link: /#manage?subscription=<id>&network=<id>  — shows one specific subscription
//   Wallet-scan: /#manage?network=<id>                  — lists all subscriptions for connected wallet

import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { NETWORK_LIST, NETWORKS, DEFAULT_NETWORK_ID } from '../config/networks';
import { connectWallet as connectExternalWallet, getProvider as getWalletProvider } from '../lib/wallet';

const SUBS_ABI = [
  'function cancelSubscription(bytes32 subscriptionId) external',
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const INTERVAL_LABEL = {
  [30  * 86400]: 'month',
  [90  * 86400]: 'quarter',
  [365 * 86400]: 'year',
};

function intervalLabel(secs) {
  return INTERVAL_LABEL[secs] ?? `${Math.round(secs / 86400)}d`;
}

function fmtAmt(n) {
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortAddr(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function parseError(err) {
  if (err.message?.includes('user rejected') || err.message?.includes('User denied')) return 'Transaction cancelled';
  if (err.message?.includes('insufficient funds')) return 'Insufficient ETH for gas fees';
  if (err.message?.includes('Only customer or merchant')) return 'Only the subscription owner can cancel';
  if (err.message?.includes('not active') || err.message?.includes('Already cancelled')) return 'Subscription is already cancelled';
  return err.message || 'Transaction failed';
}

function StatusBadge({ status }) {
  const styles = {
    active:   'bg-emerald-100 text-emerald-700',
    past_due: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  const labels = { active: 'Active', past_due: 'Past Due', cancelled: 'Cancelled' };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] ?? styles.cancelled}`}>
      {labels[status] ?? status}
    </span>
  );
}

function SubscriptionCard({ sub, onCancel, cancelling }) {
  const isCancelling = cancelling === sub.subscriptionId;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-semibold text-navy-900">{sub.plan}</div>
          <div className="text-xs text-navy-500 mt-0.5">
            by {shortAddr(sub.wallet.replace(sub.wallet, sub.wallet))} · {shortAddr(sub.wallet)}
          </div>
        </div>
        <StatusBadge status={sub.status} />
      </div>

      <div className="space-y-1.5 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-navy-500">Amount</span>
          <span className="text-navy-800 font-medium">${fmtAmt(sub.amount)} / {intervalLabel(sub.interval)}</span>
        </div>
        {sub.status === 'active' && (
          <div className="flex justify-between text-sm">
            <span className="text-navy-500">Next billing</span>
            <span className="text-navy-800">{sub.nextBilling}</span>
          </div>
        )}
        {sub.status === 'cancelled' && sub.endDate && sub.endDate !== '-' && (
          <div className="flex justify-between text-sm">
            <span className="text-navy-500">Cancelled</span>
            <span className="text-navy-800">{sub.endDate}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-navy-500">Started</span>
          <span className="text-navy-800">{sub.startDate}</span>
        </div>
      </div>

      {sub.status !== 'cancelled' && (
        <button
          onClick={() => onCancel(sub)}
          disabled={isCancelling}
          className="w-full py-2 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isCancelling ? 'Cancelling…' : 'Cancel Subscription'}
        </button>
      )}
    </div>
  );
}

function ConfirmModal({ sub, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <h3 className="text-base font-semibold text-navy-900 mb-2">Cancel subscription?</h3>
        <p className="text-sm text-navy-500 mb-1">
          <span className="font-medium text-navy-700">{sub.plan}</span> — ${fmtAmt(sub.amount)} / {intervalLabel(sub.interval)}
        </p>
        <p className="text-sm text-red-600 mb-5">
          This will cancel your subscription immediately. This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-navy-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Keep subscription
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 transition-colors"
          >
            {loading ? 'Cancelling…' : 'Yes, cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ManageSubscriptions({ defaultNetworkId, defaultSubscriptionId }) {
  const [networkId, setNetworkId] = useState(defaultNetworkId || DEFAULT_NETWORK_ID);
  const [walletAddress, setWalletAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [subscriptions, setSubscriptions] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSub, setConfirmSub] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  const network = NETWORKS[networkId];
  const isDeepLink = !!defaultSubscriptionId;

  // Load subscriptions for a wallet (wallet-scan mode)
  const loadForWallet = useCallback(async (address, netId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/customer/${address}/subscriptions?network=${netId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubscriptions(await res.json());
    } catch (err) {
      setError('Failed to load subscriptions. Please try again.');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load a single subscription by ID (deep-link mode)
  // Polls with backoff because the indexer may lag up to ~30s behind the chain.
  const loadById = useCallback(async (subId, netId) => {
    setLoading(true);
    setError(null);
    const delays = [2000, 4000, 6000, 8000, 10000, 15000, 15000]; // ~60s total
    let lastErr = null;
    for (let i = 0; i <= delays.length; i++) {
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/${subId}?network=${netId}`);
        if (res.status === 404 && i < delays.length) {
          await new Promise(r => setTimeout(r, delays[i]));
          continue;
        }
        if (!res.ok) throw new Error(res.status === 404 ? 'Subscription not found' : `HTTP ${res.status}`);
        const data = await res.json();
        setSubscriptions([data]);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        if (i < delays.length) {
          await new Promise(r => setTimeout(r, delays[i]));
        }
      }
    }
    setError(lastErr?.message || 'Subscription not found.');
    setSubscriptions([]);
    setLoading(false);
  }, []);

  // Initial load for deep-link mode (no wallet required to view)
  useEffect(() => {
    if (isDeepLink) {
      loadById(defaultSubscriptionId, networkId);
    }
  }, [isDeepLink, defaultSubscriptionId, networkId, loadById]);

  // Re-fetch when network changes in wallet-scan mode
  useEffect(() => {
    if (!isDeepLink && walletAddress) {
      loadForWallet(walletAddress, networkId);
    }
  }, [networkId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function connectWallet() {
    setConnecting(true);
    setError(null);
    try {
      const walletProvider = await connectExternalWallet();
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      setWalletAddress(address);
      if (!isDeepLink) {
        await loadForWallet(address, networkId);
      }
    } catch (err) {
      setError(parseError(err));
    } finally {
      setConnecting(false);
    }
  }

  async function executeCancel(sub) {
    const walletProvider = getWalletProvider();
    if (!walletProvider) return;
    setCancelling(sub.subscriptionId);
    setCancelError(null);
    try {
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();

      if (!network?.subsAddress) throw new Error('Contract not deployed on this network');

      const subs = new ethers.Contract(network.subsAddress, SUBS_ABI, signer);
      const tx = await subs.cancelSubscription(sub.subscriptionId, { gasLimit: 150000 });
      await tx.wait();

      setConfirmSub(null);

      // Optimistically mark as cancelled — indexer lags behind chain so
      // an immediate reload would revert the status back to active.
      setSubscriptions(prev =>
        prev?.map(s =>
          s.subscriptionId === sub.subscriptionId ? { ...s, status: 'cancelled' } : s
        )
      );
    } catch (err) {
      setCancelError(parseError(err));
      setConfirmSub(null);
    } finally {
      setCancelling(null);
    }
  }

  // In deep-link mode: does the connected wallet own this subscription?
  const deepSub = isDeepLink && subscriptions?.length ? subscriptions[0] : null;
  const walletOwns = deepSub && walletAddress
    ? deepSub.wallet.toLowerCase() === walletAddress.toLowerCase()
    : null;

  const activeNetworks = NETWORK_LIST.filter(n => n.subsAddress);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">

        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-navy-900">Manage Subscription</h1>
          <p className="text-sm text-navy-500 mt-1">
            {isDeepLink ? 'View or cancel your subscription' : 'Connect your wallet to view your subscriptions'}
          </p>
        </div>

        {/* Network selector (wallet-scan mode only) */}
        {!isDeepLink && (
          <div className="mb-4">
            <select
              value={networkId}
              onChange={e => setNetworkId(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-navy-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {activeNetworks.map(n => (
                <option key={n.id} value={n.id}>{n.name}{n.testnet ? ' (Testnet)' : ''}</option>
              ))}
            </select>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {cancelError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {cancelError}
          </div>
        )}

        {/* Deep-link mode: show sub (no wallet needed to view) */}
        {isDeepLink && (
          <>
            {loading && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-navy-500">Loading subscription…</p>
                <p className="text-xs text-navy-400 mt-1">This may take a moment to confirm on-chain.</p>
              </div>
            )}

            {!loading && deepSub && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-navy-900">{deepSub.plan}</div>
                      <div className="text-xs text-navy-500 mt-0.5 font-mono">{shortAddr(deepSub.wallet)}</div>
                    </div>
                    <StatusBadge status={deepSub.status} />
                  </div>

                  <div className="space-y-1.5 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-navy-500">Amount</span>
                      <span className="text-navy-800 font-medium">${fmtAmt(deepSub.amount)} / {intervalLabel(deepSub.interval)}</span>
                    </div>
                    {deepSub.status === 'active' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-navy-500">Next billing</span>
                        <span className="text-navy-800">{deepSub.nextBilling}</span>
                      </div>
                    )}
                    {deepSub.status === 'cancelled' && deepSub.endDate && deepSub.endDate !== '-' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-navy-500">Cancelled</span>
                        <span className="text-navy-800">{deepSub.endDate}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-navy-500">Started</span>
                      <span className="text-navy-800">{deepSub.startDate}</span>
                    </div>
                  </div>

                  {deepSub.status !== 'cancelled' && !walletAddress && (
                    <button
                      onClick={connectWallet}
                      disabled={connecting}
                      className="w-full py-2.5 rounded-xl text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 transition-colors"
                    >
                      {connecting ? 'Connecting…' : 'Connect Wallet to Cancel'}
                    </button>
                  )}

                  {deepSub.status !== 'cancelled' && walletAddress && walletOwns && (
                    <button
                      onClick={() => setConfirmSub(deepSub)}
                      disabled={!!cancelling}
                      className="w-full py-2.5 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      {cancelling ? 'Cancelling…' : 'Cancel Subscription'}
                    </button>
                  )}

                  {deepSub.status !== 'cancelled' && walletAddress && walletOwns === false && (
                    <p className="text-sm text-center text-navy-500 pt-1">
                      Connected wallet doesn't own this subscription.
                    </p>
                  )}
                </div>

                {walletAddress && (
                  <p className="text-xs text-center text-navy-400">
                    Connected: <span className="font-mono">{shortAddr(walletAddress)}</span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Wallet-scan mode */}
        {!isDeepLink && (
          <>
            {!walletAddress && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 11v2" />
                    <path d="M2 11h20" />
                    <path d="M7 3l5-1 5 1" />
                  </svg>
                </div>
                <h2 className="text-base font-semibold text-navy-900 mb-1">Connect your wallet</h2>
                <p className="text-sm text-navy-500 mb-5">We'll look up all subscriptions for your wallet address.</p>
                <button
                  onClick={connectWallet}
                  disabled={connecting}
                  className="w-full py-2.5 rounded-xl text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 transition-colors"
                >
                  {connecting ? 'Connecting…' : 'Connect Wallet'}
                </button>
              </div>
            )}

            {walletAddress && loading && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-navy-500">Loading subscriptions…</p>
              </div>
            )}

            {walletAddress && !loading && subscriptions !== null && (
              <div className="space-y-4">
                <p className="text-xs text-center text-navy-400">
                  Connected: <span className="font-mono">{shortAddr(walletAddress)}</span>
                </p>

                {subscriptions.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                    <p className="text-sm text-navy-500">No subscriptions found for this wallet.</p>
                  </div>
                ) : (
                  subscriptions.map(sub => (
                    <SubscriptionCard
                      key={sub.subscriptionId}
                      sub={sub}
                      onCancel={setConfirmSub}
                      cancelling={cancelling}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Cancel confirmation modal */}
        {confirmSub && (
          <ConfirmModal
            sub={confirmSub}
            loading={!!cancelling}
            onConfirm={() => executeCancel(confirmSub)}
            onCancel={() => setConfirmSub(null)}
          />
        )}
      </div>
    </div>
  );
}
